import React, { useState } from 'react';
import { Box, CircularProgress, Stack, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  AppTask,
  TaskType,
  isConnectionUnauthorizedMetadata,
} from '@domain/tasks/models/Task';
import { Connection } from '@domain/connection/models/Connection';
import { APP_CONFIG } from '../../../../config/app.config';
import DashboardCardHeader from '../general/DashboardCardHeader';
import { useTasks } from '../../hooks/useTasks';
import { useConnections } from '@features/connections/generic/hooks/useConnections';
import { useToast } from '@shared/hooks/useToast';
import { useBottomSheet } from '@shared/shell/bottomsheet/useBottomSheet';
import { CreatePurchaseOrderFlow } from '@features/purchasing/components/create/CreatePurchaseOrderFlow';
import ForecastChangedDialog from './forecast-changed';
import TaskItem from './TaskItem';
import { taskPath } from './utils';
import ReconnectConnectionDialog from '@features/connections/generic/components/ReconnectConnectionDialog';

const DashboardTasksSidebar: React.FC = () => {
  const colors = APP_CONFIG.ui.theme.colors;
  const navigate = useNavigate();
  const { success, error: showError } = useToast();
  const { openBottomSheet, closeBottomSheet } = useBottomSheet();
  const { tasks, isLoading, acknowledgeTask, isAcknowledging } = useTasks(true);
  const { connections } = useConnections();
  const [forecastTask, setForecastTask] = useState<AppTask | null>(null);
  const [reconnectConnection, setReconnectConnection] = useState<Connection | null>(null);

  const handleTaskClick = (task: AppTask) => {
    if (task.type === TaskType.FORECAST_CHANGED) {
      setForecastTask(task);
      return;
    }
    if (task.type === TaskType.REPLENISH) {
      openBottomSheet({
        title: 'Purchase Order Draft',
        component: (
          <CreatePurchaseOrderFlow
            needsRestock
            handleClose={async () => {
              await closeBottomSheet();
            }}
          />
        ),
      });
      return;
    }
    if (
      task.type === TaskType.CONNECTION_UNAUTHORIZED &&
      isConnectionUnauthorizedMetadata(task.metadata)
    ) {
      const connection = connections?.find(
        (item) => item.id === task.metadata.connectionId,
      );
      if (connection) {
        setReconnectConnection(connection);
        return;
      }
    }
    navigate(taskPath(task.type));
  };

  const handleAcknowledge = async (taskId: string) => {
    if (!forecastTask) return;
    try {
      await acknowledgeTask(taskId);
    } catch (error) {
      showError(error instanceof Error ? error.message : 'Failed to acknowledge task');
      throw error;
    }
  };

  const handleAllChecked = () => {
    success('Forecast change acknowledged');
  };

  return (
    <Stack gap={3} width="100%" sx={{ maxWidth: { lg: 458 } }}>
      <Box
        sx={{
          border: `1px solid ${colors.border}`,
          borderRadius: '16px',
          overflow: 'hidden',
          width: '100%',
        }}
      >
        <DashboardCardHeader title="Tasks for this week" divider />

        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        )}

        {!isLoading && tasks.length === 0 && (
          <Stack
            sx={{ p: 3 }}
            gap={2}
            direction="row"
            alignItems="center"
            justifyContent="flex-start"
          >
            <Typography component="span" sx={{ fontSize: 22, lineHeight: 1 }}>
              🎉
            </Typography>
            <Typography variant="body2" fontWeight={500} color="text.secondary">
              You're all caught up for this week.
            </Typography>
          </Stack>
        )}

        {tasks.map((task) => (
          <TaskItem key={task.sourceIds.join('-')} task={task} onClick={handleTaskClick} />
        ))}
      </Box>

      <ForecastChangedDialog
        open={Boolean(forecastTask)}
        task={forecastTask}
        isAcknowledging={isAcknowledging}
        onClose={() => setForecastTask(null)}
        onAcknowledge={handleAcknowledge}
        onAllChecked={handleAllChecked}
      />

      <ReconnectConnectionDialog
        connection={reconnectConnection}
        open={Boolean(reconnectConnection)}
        onClose={() => setReconnectConnection(null)}
      />
    </Stack>
  );
};

export default DashboardTasksSidebar;
